import { listPatients } from "@/core/patients/patient-store";
import {
  apiListConsultations,
  apiListPrescriptions,
  apiSyncConsultation,
  apiSyncPrescription,
} from "@/core/hospitol-api-client";
import {
  markPharmacySotMigratedIfRemoteConfirmed,
  probeHospitolDurableConfigured,
  shouldSeedPharmacyDemoData,
} from "@/core/pharmacy/pharmacy-durable-sot";
import {
  completeActiveTicketsForPatient,
  completeQueueTicketByConsultation,
  enqueueTicket,
  listQueueTickets,
} from "@/core/queue/service-queue-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import type { PharmacyPrefill } from "./pharmacy-handoff";
import type {
  Consultation,
  ConsultationInput,
  ConsultationStatus,
  ConsultationVitalsInput,
  Prescription,
  PrescriptionLine,
  PrescriptionLineFulfillment,
  PrescriptionLineInput,
  PrescriptionLineDispenseState,
  PrescriptionLineStockState,
  PrescriptionStatus,
} from "./types";
import {
  buildConsultationVitals,
  normalizeConsultationVitals,
  normalizePrescriptionTiming,
} from "./types";
import type { PrescriptionFrequency } from "./types";
import { emptyPrescriptionLineFulfillment } from "./types";

const CONSULTATIONS_KEY = "hospitol.consultations";
const PRESCRIPTIONS_KEY = "hospitol.prescriptions";
const DEMO_SEED_KEY = "hospitol.clinical.demoSeeded";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  setStorageItem(key, JSON.stringify(value));
}

function writePrescriptions(list: Prescription[]): void {
  writeJson(PRESCRIPTIONS_KEY, list);
  window.dispatchEvent(new CustomEvent("hospitol:clinical-prescriptions"));
}

function replicatePrescriptionToApi(prescription: Prescription): void {
  void apiSyncPrescription(prescription);
}

const FREQUENCY_VALUES = new Set<PrescriptionFrequency>([
  "once_daily",
  "twice_daily",
  "thrice_daily",
  "four_times_daily",
  "every_8h",
  "every_12h",
  "as_needed",
  "once",
]);

function normalizePrescriptionLine(raw: Record<string, unknown>): PrescriptionLine {
  const duration = Number(raw.durationDays);
  const freq = String(raw.frequency ?? "");
  const fulfillmentRaw = raw.fulfillment as Record<string, unknown> | undefined;
  const stockState = fulfillmentRaw?.stockState as PrescriptionLineStockState | undefined;
  const dispenseState = fulfillmentRaw?.dispenseState as PrescriptionLineDispenseState | undefined;
  const validStock = new Set<PrescriptionLineStockState>([
    "unchecked",
    "reserved",
    "shortage",
    "issued",
  ]);
  const validDispense = new Set<PrescriptionLineDispenseState>([
    "pending",
    "ready",
    "delivered",
    "external",
  ]);
  return {
    id: String(raw.id ?? ""),
    productId: String(raw.productId ?? ""),
    productLabel: String(raw.productLabel ?? ""),
    quantity: Math.max(1, Number(raw.quantity) || 1),
    frequency: FREQUENCY_VALUES.has(freq as PrescriptionFrequency)
      ? (freq as PrescriptionFrequency)
      : "",
    durationDays: Number.isFinite(duration) && duration > 0 ? duration : null,
    timing: normalizePrescriptionTiming(raw.timing),
    instructions: String(raw.instructions ?? ""),
    fulfillment: {
      stockState: stockState && validStock.has(stockState) ? stockState : "unchecked",
      dispenseState:
        dispenseState && validDispense.has(dispenseState) ? dispenseState : "pending",
      billingDocumentId: fulfillmentRaw?.billingDocumentId
        ? String(fulfillmentRaw.billingDocumentId)
        : null,
    },
  };
}

function normalizePrescription(raw: Record<string, unknown>): Prescription {
  const lines = Array.isArray(raw.lines)
    ? raw.lines.map((line) => normalizePrescriptionLine(line as Record<string, unknown>))
    : [];
  return {
    id: String(raw.id ?? ""),
    consultationId: String(raw.consultationId ?? ""),
    patientId: String(raw.patientId ?? ""),
    lines,
    status: (raw.status as PrescriptionStatus) ?? "draft",
    source: raw.source as Prescription["source"],
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    sentAt: raw.sentAt ? String(raw.sentAt) : null,
  };
}

function readPrescriptions(): Prescription[] {
  return readJson<Record<string, unknown>[]>(PRESCRIPTIONS_KEY, []).map(normalizePrescription);
}

function lineId(): string {
  return `rxl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function sortByCreatedDesc<T extends { createdAt: string }>(a: T, b: T): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function normalizeConsultation(raw: Record<string, unknown>): Consultation {
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    motif: String(raw.motif ?? ""),
    diagnostic: String(raw.diagnostic ?? ""),
    acts: String(raw.acts ?? ""),
    vitals: normalizeConsultationVitals(raw.vitals),
    status: (raw.status as ConsultationStatus) ?? "open",
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    closedAt: raw.closedAt ? String(raw.closedAt) : null,
  };
}

function readConsultations(): Consultation[] {
  return readJson<Record<string, unknown>[]>(CONSULTATIONS_KEY, []).map(normalizeConsultation);
}

function replaceConsultationsCache(consultations: Consultation[]): void {
  writeJson(CONSULTATIONS_KEY, consultations);
}

function replicateConsultationToApi(consultation: Consultation): void {
  // Pharmacy edition returns 403 — hospitolFetch yields null; LS remains cache.
  void apiSyncConsultation(consultation);
}

function mergeApiConsultation(localId: string, apiConsultation: Consultation): void {
  const list = readConsultations();
  const index = list.findIndex((c) => c.id === localId);
  if (index >= 0) {
    list[index] = apiConsultation;
    writeJson(CONSULTATIONS_KEY, list);
  }
}

export function ensureDemoClinicalData() {
  if (!shouldAutoSeedDemoData()) return;
  if (!shouldSeedPharmacyDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const patients = listPatients();
  const patient = patients[0];
  if (!patient) return;

  createConsultation({
    patientId: patient.id,
    motif: "Fièvre et céphalées",
    diagnostic: "Syndrome grippal probable",
    acts: "Consultation générale, prise des constantes",
    vitals: {
      bloodPressureSystolic: 118,
      bloodPressureDiastolic: 76,
      heartRate: 88,
      temperatureC: 38.2,
      respiratoryRate: 18,
      oxygenSaturation: 97,
      weightKg: 72,
      painScore: 4,
    },
  });

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listConsultations(): Consultation[] {
  ensureDemoClinicalData();
  return readConsultations().sort(sortByCreatedDesc);
}

export function listConsultationsByPatient(patientId: string): Consultation[] {
  return listConsultations().filter((c) => c.patientId === patientId);
}

export function getConsultationById(id: string): Consultation | null {
  return listConsultations().find((c) => c.id === id) ?? null;
}

export function createConsultation(input: ConsultationInput): Consultation {
  const now = new Date().toISOString();
  const vitals =
    input.vitals === null
      ? null
      : input.vitals
        ? buildConsultationVitals(input.vitals)
        : null;
  const consultation: Consultation = {
    id: `con-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    motif: input.motif.trim(),
    diagnostic: input.diagnostic?.trim() ?? "",
    acts: input.acts?.trim() ?? "",
    vitals,
    status: "open",
    createdAt: now,
    updatedAt: now,
    closedAt: null,
  };

  const consultations = readConsultations();
  consultations.unshift(consultation);
  writeJson(CONSULTATIONS_KEY, consultations);

  const prescription: Prescription = {
    id: `rx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    consultationId: consultation.id,
    patientId: consultation.patientId,
    lines: [],
    status: "draft",
    createdAt: now,
    updatedAt: now,
    sentAt: null,
  };
  const prescriptions = readPrescriptions();
  prescriptions.unshift(prescription);
  writePrescriptions(prescriptions);
  replicatePrescriptionToApi(prescription);

  // Upsert with local id (preserves consultationId link on Rx).
  void apiSyncConsultation(consultation).then((apiConsultation) => {
    if (apiConsultation) mergeApiConsultation(consultation.id, apiConsultation);
  });

  return consultation;
}

const PHARMACY_DIRECT_MOTIF = "Ordonnance officine";
const PHARMACY_DIRECT_ACTS = "Pharmacie autonome — saisie directe par le pharmacien";

/** Crée une ordonnance sans passage médecin (pharmacie autonome, si activé par l'admin). */
export function createPharmacistDirectPrescription(patientId: string): {
  consultation: Consultation;
  prescription: Prescription;
} {
  const consultation = createConsultation({
    patientId,
    motif: PHARMACY_DIRECT_MOTIF,
    acts: PHARMACY_DIRECT_ACTS,
  });
  const prescription = getPrescriptionByConsultation(consultation.id);
  if (!prescription) {
    throw new Error("Ordonnance introuvable après création.");
  }
  const tagged: Prescription = { ...prescription, source: "pharmacy_direct" };
  savePrescription(tagged);
  return { consultation, prescription: tagged };
}

export function isPharmacistDirectPrescription(rx: Prescription): boolean {
  return rx.source === "pharmacy_direct";
}

export function updateConsultation(
  id: string,
  patch: Partial<Pick<Consultation, "motif" | "diagnostic" | "acts">> & {
    vitals?: ConsultationVitalsInput | null;
  },
): Consultation | null {
  const list = readConsultations();
  const index = list.findIndex((c) => c.id === id);
  if (index < 0) return null;

  let vitals = list[index]!.vitals;
  if (patch.vitals !== undefined) {
    vitals =
      patch.vitals === null ? null : buildConsultationVitals(patch.vitals);
  }

  const updated: Consultation = {
    ...list[index]!,
    motif: patch.motif?.trim() ?? list[index]!.motif,
    diagnostic: patch.diagnostic?.trim() ?? list[index]!.diagnostic,
    acts: patch.acts?.trim() ?? list[index]!.acts,
    vitals,
    updatedAt: new Date().toISOString(),
  };
  list[index] = updated;
  writeJson(CONSULTATIONS_KEY, list);
  replicateConsultationToApi(updated);
  return updated;
}

export function setConsultationStatus(
  id: string,
  status: ConsultationStatus,
): Consultation | null {
  const list = readConsultations();
  const index = list.findIndex((c) => c.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  const updated: Consultation = {
    ...list[index]!,
    status,
    updatedAt: now,
    closedAt: status === "closed" ? now : list[index]!.closedAt,
  };
  list[index] = updated;
  writeJson(CONSULTATIONS_KEY, list);
  replicateConsultationToApi(updated);

  if (status === "closed") {
    completeQueueTicketByConsultation(id);
    completeActiveTicketsForPatient("clinical", updated.patientId);
    window.dispatchEvent(
      new CustomEvent("hospitol:consultation-closed", { detail: { consultationId: id } }),
    );
  }

  return updated;
}

/** Télécharge les consultations depuis l'API (SoT Care si durable) et met à jour le cache local. */
export async function syncConsultationsFromApi(): Promise<boolean> {
  const remote = await apiListConsultations();
  if (remote === null) return false;

  const durable = await probeHospitolDurableConfigured();

  if (remote.length > 0) {
    replaceConsultationsCache(remote.map((c) => normalizeConsultation(c as unknown as Record<string, unknown>)));
    markPharmacySotMigratedIfRemoteConfirmed("consultations", durable, remote.length);
    return true;
  }

  const local = readConsultations();
  if (local.length > 0) {
    for (const consultation of local) {
      await apiSyncConsultation(consultation);
    }
    const again = await apiListConsultations();
    if (again === null) return false;
    if (again.length > 0) {
      replaceConsultationsCache(
        again.map((c) => normalizeConsultation(c as unknown as Record<string, unknown>)),
      );
      markPharmacySotMigratedIfRemoteConfirmed("consultations", durable, again.length);
    }
    return true;
  }

  return true;
}

export function listPrescriptions(): Prescription[] {
  // WP-H2.1: seed only when probe explicitly says durable is OFF.
  if (shouldSeedPharmacyDemoData()) {
    ensureDemoClinicalData();
  }
  return readPrescriptions().sort(sortByCreatedDesc);
}

/** Pull prescriptions from API — durable SoT when DATABASE_URL configured (WP-H2). */
export async function syncPrescriptionsFromApi(): Promise<boolean> {
  const remote = await apiListPrescriptions();
  if (remote === null) return false;

  const durable = await probeHospitolDurableConfigured();
  const normalized = remote.map((rx) =>
    normalizePrescription(rx as unknown as Record<string, unknown>),
  );

  if (normalized.length > 0) {
    writePrescriptions(normalized);
    markPharmacySotMigratedIfRemoteConfirmed("prescriptions", durable, normalized.length);
    return true;
  }

  const local = readPrescriptions();
  if (local.length > 0) {
    // WP-H2.1: never silent-discard local Rx when remote empty — push then keep LS if still empty.
    for (const rx of local) {
      await apiSyncPrescription(rx);
    }
    const again = await apiListPrescriptions();
    if (again === null) return false;
    if (again.length > 0) {
      writePrescriptions(
        again.map((rx) => normalizePrescription(rx as unknown as Record<string, unknown>)),
      );
      markPharmacySotMigratedIfRemoteConfirmed("prescriptions", durable, again.length);
    }
    return true;
  }

  return true;
}

export function listPrescriptionsByPatient(patientId: string): Prescription[] {
  return listPrescriptions().filter((p) => p.patientId === patientId);
}

export function getPrescriptionById(id: string): Prescription | null {
  return listPrescriptions().find((p) => p.id === id) ?? null;
}

export function getPrescriptionByConsultation(consultationId: string): Prescription | null {
  return listPrescriptions().find((p) => p.consultationId === consultationId) ?? null;
}

function savePrescription(prescription: Prescription): void {
  const list = readPrescriptions();
  const index = list.findIndex((p) => p.id === prescription.id);
  if (index < 0) return;
  list[index] = prescription;
  writePrescriptions(list);
  replicatePrescriptionToApi(prescription);
}

export function addPrescriptionLine(
  prescriptionId: string,
  input: PrescriptionLineInput,
): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;

  const line: PrescriptionLine = {
    id: lineId(),
    productId: input.productId,
    productLabel: input.productLabel.trim(),
    quantity: Math.max(1, input.quantity),
    frequency: input.frequency ?? "",
    durationDays:
      input.durationDays != null && input.durationDays > 0 ? input.durationDays : null,
    timing: input.timing ?? [],
    instructions: input.instructions?.trim() ?? "",
    fulfillment: emptyPrescriptionLineFulfillment(),
  };

  const updated: Prescription = {
    ...prescription,
    lines: [...prescription.lines, line],
    updatedAt: new Date().toISOString(),
  };
  savePrescription(updated);
  return updated;
}

export function updatePrescriptionLine(
  prescriptionId: string,
  lineIdValue: string,
  patch: Partial<
    Pick<
      PrescriptionLine,
      "quantity" | "frequency" | "durationDays" | "timing" | "instructions"
    >
  >,
): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;

  const lines = prescription.lines.map((line) => {
    if (line.id !== lineIdValue) return line;
    return {
      ...line,
      quantity: patch.quantity !== undefined ? Math.max(1, patch.quantity) : line.quantity,
      frequency: patch.frequency !== undefined ? patch.frequency : line.frequency,
      durationDays:
        patch.durationDays !== undefined
          ? patch.durationDays != null && patch.durationDays > 0
            ? patch.durationDays
            : null
          : line.durationDays,
      timing: patch.timing !== undefined ? patch.timing : line.timing,
      instructions: patch.instructions?.trim() ?? line.instructions,
    };
  });

  const updated: Prescription = {
    ...prescription,
    lines,
    updatedAt: new Date().toISOString(),
  };
  savePrescription(updated);
  return updated;
}

export function patchPrescriptionLineFulfillment(
  prescriptionId: string,
  lineIdValue: string,
  patch: Partial<PrescriptionLineFulfillment>,
): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;

  const lines = prescription.lines.map((line) => {
    if (line.id !== lineIdValue) return line;
    return {
      ...line,
      fulfillment: { ...line.fulfillment, ...patch },
    };
  });

  const updated: Prescription = {
    ...prescription,
    lines,
    updatedAt: new Date().toISOString(),
  };
  savePrescription(updated);
  return updated;
}

export function removePrescriptionLine(
  prescriptionId: string,
  lineIdValue: string,
): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;

  const updated: Prescription = {
    ...prescription,
    lines: prescription.lines.filter((l) => l.id !== lineIdValue),
    updatedAt: new Date().toISOString(),
  };
  savePrescription(updated);
  return updated;
}

export function buildPharmacyPrefill(prescriptionId: string): PharmacyPrefill | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription || prescription.lines.length === 0) return null;

  return {
    patientId: prescription.patientId,
    prescriptionId: prescription.id,
    lines: prescription.lines.map((line) => ({
      productId: line.productId,
      quantity: line.quantity,
      label: line.productLabel,
    })),
  };
}

export function markPrescriptionSent(prescriptionId: string): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;
  if (
    prescription.status === "pharmacist_validated" ||
    prescription.status === "validated_for_billing" ||
    prescription.status === "dispensed"
  ) {
    return prescription;
  }

  const wasDraft = prescription.status === "draft";
  const now = new Date().toISOString();
  const updated: Prescription = {
    ...prescription,
    status: "sent_to_pharmacy",
    sentAt: now,
    updatedAt: now,
  };
  savePrescription(updated);

  if (wasDraft && prescription.source !== "pharmacy_direct") {
    const consultation = getConsultationById(prescription.consultationId);
    const alreadyWaiting = listQueueTickets({
      serviceId: "pharmacy",
      status: ["waiting", "called", "in_progress"],
    }).some((t) => t.patientId === prescription.patientId);
    if (!alreadyWaiting) {
      enqueueTicket({
        patientId: prescription.patientId,
        serviceId: "pharmacy",
        unitName: "Pharmacie",
        motif: consultation?.motif ?? "Ordonnance à délivrer",
        priority: 10,
      });
    }
  }

  return updated;
}

export function markPrescriptionDispensed(prescriptionId: string): void {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return;

  savePrescription({
    ...prescription,
    status: "dispensed",
    updatedAt: new Date().toISOString(),
  });

  completeActiveTicketsForPatient("pharmacy", prescription.patientId);
}

export function markPrescriptionPharmacistValidated(prescriptionId: string): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;
  if (
    prescription.status === "dispensed" ||
    prescription.status === "validated_for_billing"
  ) {
    return prescription;
  }

  const updated: Prescription = {
    ...prescription,
    status: "pharmacist_validated",
    updatedAt: new Date().toISOString(),
  };
  savePrescription(updated);
  return updated;
}

export function markPrescriptionTransferredToBilling(
  prescriptionId: string,
): Prescription | null {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;

  const updated: Prescription = {
    ...prescription,
    status: "validated_for_billing",
    updatedAt: new Date().toISOString(),
  };
  savePrescription(updated);
  completeActiveTicketsForPatient("pharmacy", prescription.patientId);
  return updated;
}

export function listPharmacyQueuePrescriptions(): Prescription[] {
  return listPrescriptions().filter(
    (p) =>
      p.lines.length > 0 &&
      (p.status === "sent_to_pharmacy" || p.status === "pharmacist_validated"),
  );
}

export function pharmacyOrderPath(prescriptionId: string): string {
  return `/tenant/hospitol/pharmacy/orders/${prescriptionId}`;
}

export function getPharmacyPrescriptionForPatient(patientId: string): Prescription | null {
  return (
    listPrescriptions().find(
      (p) =>
        p.patientId === patientId &&
        p.lines.length > 0 &&
        (p.status === "sent_to_pharmacy" || p.status === "pharmacist_validated"),
    ) ?? null
  );
}

export function isPrescriptionReadyToDispense(prescriptionId: string): boolean {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return false;
  return prescription.status === "pharmacist_validated";
}

export function consultationDetailPath(id: string): string {
  return `/tenant/hospitol/clinical/consultations/${id}`;
}

export function countOpenConsultations(): number {
  return listConsultations().filter((c) => c.status === "open").length;
}

export function countPendingPrescriptions(): number {
  return listPrescriptions().filter(
    (p) =>
      (p.status === "draft" || p.status === "sent_to_pharmacy") && p.lines.length > 0,
  ).length;
}
